import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  EmailTemplateDto,
  getEmailTemplates,
} from "../../services/emails.service.ts";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import EmptyImage from "../../components/EmptyImage.tsx";
import { fixedHeadStyle, fixedStyle } from "../../utils/constanst.js";

const EmailTemplatesPage: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [templates, setTemplates] = useState<EmailTemplateDto[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<string>("id");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const navigate = useNavigate();
  useAppSettings();

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const offset = page * limit;
      const response = await getEmailTemplates({
        limit,
        offset,
        search: debouncedSearchTerm || undefined,
        sort: sortBy,
        order: sortOrder,
      });
      setTemplates(response.results);
      setTotal(response.total);
    } catch (error) {
      console.error("Error cargando plantillas de correo", error);
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearchTerm, sortBy, sortOrder]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    if (searchTerm === "") {
      setDebouncedSearchTerm("");
      setPage(0);
      return;
    }

    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(0);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
  };

  const handleNewTemplate = () => {
    navigate("/emails/plantillas/nuevo");
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setPage(0);
  };

  const getIconSort = (field: string) => {
    if (sortBy !== field) return null;
    return (
      <i
        className={`fa-solid fa-sort-${sortOrder === "asc" ? "up" : "down"}`}
      />
    );
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLimit = parseInt(e.target.value);
    setLimit(newLimit);
    setPage(0);
  };

  const startRecord = page * limit + 1;
  const endRecord = Math.min(page * limit + templates.length, total);

  return (
    <Layout loading={loading}>
      <div>
        <Title
          baseTitle="Correos"
          basePath="/emails/plantillas"
          activeTitle="Plantillas de correos"
          title="Plantillas de correos"
        />

        <div className="m-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="input-group w-50">
              <input
                type="text"
                className="form-control"
                placeholder="Buscar por nombre, asunto o descripción..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <button
                className="btn btn-verde"
                type="button"
                onClick={handleNewTemplate}
              >
                Nueva plantilla
              </button>
            </div>
          </div>

          <div
            style={{
              background: "#ffffff",
              borderRadius: "20px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
              border: "1px solid #e5e7eb",
              overflow: "hidden",
            }}
          >
            <div
              className="table-responsive"
              style={{ overflowY: "auto", height: "500px" }}
            >
              <table className="table table-hover table-panel text-nowrap align-middle mb-0">
                <thead>
                  <tr style={{ ...fixedHeadStyle } as React.CSSProperties}>
                    <th
                      onClick={() => handleSort("id")}
                      style={
                        {
                          cursor: "pointer",
                          ...fixedStyle,
                        } as React.CSSProperties
                      }
                    >
                      ID {getIconSort("id")}
                    </th>
                    <th
                      onClick={() => handleSort("name")}
                      style={{ cursor: "pointer" }}
                    >
                      Nombre {getIconSort("name")}
                    </th>
                    <th
                      onClick={() => handleSort("subject")}
                      style={{ cursor: "pointer" }}
                    >
                      Asunto {getIconSort("subject")}
                    </th>
                    <th
                      onClick={() => handleSort("description")}
                      style={{ cursor: "pointer" }}
                    >
                      Descripción {getIconSort("description")}
                    </th>
                    <th style={{ cursor: "default" }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.length > 0 ? (
                    templates.map((t) => (
                      <tr key={t.id}>
                        <td style={{ ...fixedStyle } as React.CSSProperties}>
                          {t.id}
                        </td>
                        <td className="fw-semibold">{t.name}</td>
                        <td className="text-truncate" style={{ maxWidth: 260 }}>
                          {t.subjectTemplate}
                        </td>
                        <td className="text-truncate" style={{ maxWidth: 300 }}>
                          {t.description || "-"}
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/emails/plantillas/${t.id}`);
                            }}
                          >
                            <i className="fa-solid fa-pen"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={5}
                        style={{
                          background: "#fff",
                          padding: "60px 20px",
                        }}
                      >
                        <EmptyImage
                          className="mx-auto w-250px"
                          text={
                            searchTerm
                              ? "No se encontraron plantillas"
                              : "Aún no hay plantillas configuradas"
                          }
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Paginación */}
          <div className="d-md-flex align-items-center mt-3">
            <div className="me-md-auto text-md-left text-center mb-2 mb-md-0">
              Mostrando {startRecord} - {endRecord} de {total} plantillas
            </div>
            <div className="d-flex align-items-center">
              <span className="me-2">Mostrar:</span>
              <select
                className="form-select me-3"
                value={limit}
                onChange={handleLimitChange}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <ul className="pagination mb-0 justify-content-center">
              <li className={`page-item ${page === 0 && "disabled"}`}>
                <a
                  href="#/"
                  className="page-link"
                  onClick={(e) => {
                    e.preventDefault();
                    if (page > 0) setPage(page - 1);
                  }}
                >
                  Anterior
                </a>
              </li>
              <li className={`page-item ${endRecord >= total && "disabled"}`}>
                <a
                  href="#/"
                  className="page-link"
                  onClick={(e) => {
                    e.preventDefault();
                    if (endRecord < total) setPage(page + 1);
                  }}
                >
                  Siguiente
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </Layout>
  );
};

const styles: { [key: string]: React.CSSProperties } = {};

export default EmailTemplatesPage;
